import { Component, computed, DestroyRef, effect, inject, LOCALE_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Filters } from './filters/filters';
import { OrdersCardsList } from './orders-cards-list/orders-cards-list';
import { OrderFilters, OrderListItem } from '../../../../pages/admin-dashboard/orders-management/orders.model';
import { OrderService } from '../../../../services/orders/order.service';
import { PharmacyCreditService } from '../../../../services/pharmacy-credit/pharmacy-credit';
import { Loading } from '../../../../utils/loading/loading';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';

// const MOCK_ORDERS: OrderListItem[] = [
//   {
//     id: 12345,
//     status: { titleEn: 'Pending', titleAr: 'قيد الانتظار' },
//     pharmacy: { pharmacyOwner: { fullName: 'Ahmed Ali' } },
//     region: { titleEn: 'Nasr City', titleAr: 'مدينة نصر' },
//     totalItems: 5,
//     totalPharmacyPrice: 1500,
//     createdOn: new Date('2026-01-15T10:30:00'),
//     createdBy: { fullName: 'Ahmed Hassan' },
//     lastModifiedOn: new Date('2026-04-16T15:45:00'),
//     lastModifiedBy: { fullName: 'Mohamed Ali' },
//   },
//   {
//     id: 12346,
//     status: { titleEn: 'Packaged', titleAr: 'تم التجهيز' },
//     pharmacy: { pharmacyOwner: { fullName: 'Cairo Pharmacy' } },
//     region: { titleEn: 'Maadi', titleAr: 'المعادي' },
//     totalItems: 3,
//     totalPharmacyPrice: 850,
//     createdOn: new Date('2026-01-16T14:15:00'),
//     createdBy: { fullName: 'Sara Mohamed' },
//     lastModifiedOn: new Date('2026-04-17T09:20:00'),
//     lastModifiedBy: { fullName: 'Ahmed Hassan' },
//   },
//   {
//     id: 12347,
//     status: { titleEn: 'Delivered', titleAr: 'تم التوصيل' },
//     pharmacy: { pharmacyOwner: { fullName: 'Downtown Pharmacy' } },
//     region: { titleEn: 'Heliopolis', titleAr: 'مصر الجديدة' },
//     totalItems: 8,
//     totalPharmacyPrice: 2300,
//     createdOn: new Date('2026-01-17T09:00:00'),
//     createdBy: { fullName: 'Mohamed Ali' },
//     lastModifiedOn: new Date('2026-04-18T11:00:00'),
//     lastModifiedBy: { fullName: 'Sara Mohamed' },
//   },
// ] as any;

export type LoadState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-order-list-component',
  standalone: true,
  imports: [Filters, OrdersCardsList, Loading, Pagination],
  templateUrl: './order-list-component.html',
  styleUrl: './order-list-component.scss',
})
export class OrderListComponent {
  private orderService = inject(OrderService);
  private pharmacyCreditService = inject(PharmacyCreditService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  selectedPharmacy = computed(() => this.pharmacyCreditService.selectedPharmacy());
  state = signal<LoadState>('loading');
  orders = signal<OrderListItem[] | null>(null);
  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(25);
  totalItems = signal<number>(0);
  isFiltersApplied = signal<boolean>(false);
  isArabic = computed(() => this.locale.startsWith('ar'));

  filters = signal<OrderFilters>({
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
  });

  get totalPages(): number {
    return Math.ceil(this.totalItems() / this.itemsPerPage());
  }

  constructor() {
    effect(() => {
      const pharmacy = this.selectedPharmacy();
      if (pharmacy) {
        this.filters.update((f) => ({ ...f, storeId: pharmacy.id }));
        this.loadOrders();
      }
    });
  }

  loadOrders() {
    this.filters.update((f) => ({
      ...f,
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
    }));
    this.state.set('loading');

    const subscription = this.orderService.getOrders(this.filters()).subscribe({
      next: (res) => {
        this.orders.set(res.list);
        this.totalItems.set(res.total);
        this.state.set('success');
      },
      error: () => this.state.set('error'),
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  applyFilters(applied: OrderFilters) {
    this.currentPage.set(1);
    this.isFiltersApplied.set(true);
    this.filters.update((f) => ({
      ...f,
      ...applied,
      storeId: this.selectedPharmacy()?.id,
      pageNum: 0,
      pageSize: this.itemsPerPage(),
    }));
    this.loadOrders();
  }

  resetFilters() {
    this.filters.set({
      orderBy: 'LAST_MODIFICATION_DATE',
      orderDir: 'DESC',
      storeId: this.selectedPharmacy()?.id,
    });
    this.isFiltersApplied.set(false);
    this.currentPage.set(1);
    this.loadOrders();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      this.loadOrders();
    }
  }

  onPageSizeChange() {
    this.currentPage.set(1);
    this.loadOrders();
  }

  navigateToDetails(id: number) {
    this.router.navigate(['/user/orders/view', id]);
  }
}
